import React, { useState } from 'react';
import { Search, SlidersHorizontal, X, ChevronDown } from 'lucide-react';

/**
 * FilterBar — universal filter panel
 *
 * Props:
 *  filters      : { search, [key]: value, dateFrom, dateTo }
 *  onChange     : (key, value) => void
 *  onReset      : () => void
 *  searchPlaceholder : string
 *  fields       : Array<{ key, label, type: 'select'|'date', options?: [{value,label}] }>
 *  activeCount  : number  (how many non-default filters are active)
 */
const FilterBar = ({
  filters = {},
  onChange,
  onReset,
  searchPlaceholder = 'Search...',
  fields = [],
  activeCount = 0,
}) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="card overflow-hidden">
      {/* ── Top bar ── */}
      <div className="flex items-center gap-3 p-3 flex-wrap">
        {/* Search */}
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            className="input-field pl-9 py-2 text-sm w-full"
            placeholder={searchPlaceholder}
            value={filters.search || ''}
            onChange={e => onChange('search', e.target.value)}
          />
          {filters.search && (
            <button
              onClick={() => onChange('search', '')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Desktop: inline filter fields */}
        <div className="hidden md:grid grid-cols-2 items-center gap-2 flex-wrap">
          {fields.map(field => (
            field.type === 'select' ? (
              <div key={field.key} className="relative">
                <select
                  value={filters[field.key] || ''}
                  onChange={e => onChange(field.key, e.target.value)}
                  className={`input-field py-2 pr-8 text-sm appearance-none cursor-pointer min-w-[130px] ${filters[field.key] ? 'border-medihub-primary dark:border-medihub-accent bg-medihub-light dark:bg-medihub-dark/20' : ''}`}
                >
                  <option value="">{field.label}</option>
                  {field.options?.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
              </div>
            ) : field.type === 'date' ? (
              <input
                key={field.key}
                type="date"
                value={filters[field.key] || ''}
                onChange={e => onChange(field.key, e.target.value)}
                title={field.label}
                className={`input-field py-2 text-sm cursor-pointer ${filters[field.key] ? 'border-medihub-primary dark:border-medihub-accent bg-medihub-light dark:bg-medihub-dark/20' : ''}`}
              />
            ) : null
          ))}
        </div>

        {/* Mobile: toggle button */}
        <button
          onClick={() => setOpen(o => !o)}
          className={`md:hidden flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${open || activeCount > 0 ? 'bg-medihub-primary text-white border-medihub-primary' : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
        >
          <SlidersHorizontal className="w-4 h-4" />
          Filters
          {activeCount > 0 && (
            <span className="ml-0.5 bg-white text-medihub-primary text-xs font-bold w-4 h-4 rounded-full flex items-center justify-center">
              {activeCount}
            </span>
          )}
        </button>

        {/* Reset */}
        {/* {activeCount > 0 && (
          <button
            onClick={onReset}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 border border-red-200 dark:border-red-800 transition-colors whitespace-nowrap"
          >
            <X className="w-3.5 h-3.5" />
            Clear ({activeCount})
          </button>
        )} */}
      </div>

      {/* ── Mobile expanded filters ── */}
      {open && (
        <div className="md:hidden border-t border-gray-200 dark:border-gray-700 p-3 grid grid-cols-4 gap-2">
          {fields.map(field => (
            field.type === 'select' ? (
              <div key={field.key} className="relative">
                <select
                  value={filters[field.key] || ''}
                  onChange={e => onChange(field.key, e.target.value)}
                  className={`input-field py-2 pr-8 text-sm appearance-none w-full ${filters[field.key] ? 'border-medihub-primary bg-medihub-light dark:bg-medihub-dark/20' : ''}`}
                >
                  <option value="">{field.label}</option>
                  {field.options?.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
              </div>
            ) : field.type === 'date' ? (
              <div key={field.key}>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{field.label}</label>
                <input
                  type="date"
                  value={filters[field.key] || ''}
                  onChange={e => onChange(field.key, e.target.value)}
                  className={`input-field py-2 text-sm w-full ${filters[field.key] ? 'border-medihub-primary bg-medihub-light dark:bg-medihub-dark/20' : ''}`}
                />
              </div>
            ) : null
          ))}
        </div>
      )}

      {/* ── Active filter chips ── */}
      {/* {activeCount > 0 && (
        <div className="px-3 pb-3 flex flex-wrap gap-1.5">
          {fields.map(field => {
            const val = filters[field.key];
            if (!val) return null;
            const label = field.type === 'select'
              ? field.options?.find(o => o.value === val)?.label || val
              : `${field.label}: ${val}`;
            return (
              <span
                key={field.key}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-medihub-light dark:bg-medihub-dark/40 text-medihub-secondary dark:text-medihub-highlight rounded-full text-xs font-medium"
              >
                {label}
                <button onClick={() => onChange(field.key, '')} className="hover:text-red-500 transition-colors ml-0.5">
                  <X className="w-3 h-3" />
                </button>
              </span>
            );
          })}
          {filters.search && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-medihub-light dark:bg-medihub-dark/40 text-medihub-secondary dark:text-medihub-highlight rounded-full text-xs font-medium">
              "{filters.search}"
              <button onClick={() => onChange('search', '')} className="hover:text-red-500 transition-colors ml-0.5">
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
        </div>
      )} */}
    </div>
  );
};

export default FilterBar;
